import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ErrorEventView, ErrorSeverity } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";
import { errorEventTarget, errorKindLabel } from "@/lib/errorCodes";

const SEVERITIES: ErrorSeverity[] = ["WARNING", "ERROR", "CRITICAL"];

/**
 * Operator error overview (deliverable 3.12). Lists system failures — e.g. an
 * email that could not be sent, a failed ingestion, an unhandled server error —
 * so a human can see what went wrong, open the thing it went wrong on, and
 * acknowledge handled items.
 *
 * <p>An event is identified by its {@code code}, not by its prose: the kind
 * badge, the kind filter and the search box all key off it, so an operator can
 * ask for "every prompt-injection warning" and still get them after someone
 * rewords the summary.
 */
export default function ErrorsPage() {
  const queryClient = useQueryClient();
  const [onlyUnack, setOnlyUnack] = useState(true);
  const [severity, setSeverity] = useState<ErrorSeverity | "">("");
  const [code, setCode] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const search = useDebounced(q.trim(), 300);

  const query = new URLSearchParams();
  query.set("onlyUnacknowledged", String(onlyUnack));
  if (severity) query.set("severity", severity);
  if (code) query.set("code", code);
  if (search) query.set("q", search);

  const listQ = useQuery({
    queryKey: ["errors", onlyUnack, severity, code, search],
    queryFn: () => api.get<ErrorEventView[]>(`/api/v1/errors?${query.toString()}`),
  });

  const ackM = useMutation({
    mutationFn: (id: string) => api.post<ErrorEventView>(`/api/v1/errors/${id}/ack`),
    onSuccess: () => invalidate(queryClient),
  });
  const ackAllM = useMutation({
    mutationFn: () => api.post<{ acknowledged: number }>(`/api/v1/errors/ack-all`),
    onSuccess: () => invalidate(queryClient),
  });

  const events = listQ.data ?? [];
  const filtered = !!code || !!search;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">System errors</h1>
        <p className="text-sm text-[var(--fg-muted)]">
          Operational failures across the agent, ingestion, and API. Open an item
          to deal with it, and acknowledge it once you have.
        </p>
      </header>

      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm flex items-center gap-2">
          <input
            type="checkbox"
            checked={onlyUnack}
            onChange={(e) => setOnlyUnack(e.target.checked)}
          />
          Unacknowledged only
        </label>
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Severity</div>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as ErrorSeverity | "")}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white"
          >
            <option value="">All</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Search</div>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. prompt injection"
            aria-label="Search errors"
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white w-56"
          />
        </label>
        <button
          type="button"
          onClick={() => ackAllM.mutate()}
          disabled={ackAllM.isPending || events.length === 0}
          className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
        >
          Acknowledge all
        </button>
        <div className="text-xs text-[var(--fg-muted)] pb-1.5">
          {events.length} event{events.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Acknowledge-all ignores the filters — it clears the whole queue. Say so
          while a filter is on, so nobody reads it as "acknowledge these five". */}
      {code ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-[var(--fg-muted)]">Showing kind</span>
          <span className="inline-flex items-center gap-2 rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-700">
            {code}
            <button
              type="button"
              onClick={() => setCode(null)}
              aria-label="Clear kind filter"
              className="text-zinc-500 hover:text-zinc-900"
            >
              ×
            </button>
          </span>
        </div>
      ) : null}
      {filtered && events.length > 0 ? (
        <p className="text-xs text-[var(--fg-muted)]">
          “Acknowledge all” clears every open event, not only the ones shown here.
        </p>
      ) : null}

      <Card>
        <CardTitle>Errors</CardTitle>
        {listQ.isLoading ? (
          <div className="text-sm text-[var(--fg-muted)]">Loading…</div>
        ) : listQ.isError ? (
          <div className="text-sm text-rose-700">Could not load errors.</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-[var(--fg-muted)]">
            {filtered
              ? "No errors match this search."
              : `No errors${onlyUnack ? " awaiting acknowledgement" : ""}. 🎉`}
          </div>
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {events.map((e) => {
              const target = errorEventTarget(e);
              return (
                <li key={e.id} className="py-3 flex items-start gap-3">
                  <SeverityPill severity={e.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <KindBadge
                        label={errorKindLabel(e)}
                        code={e.code}
                        onFilter={() => setCode(e.code)}
                      />
                      <span className="text-sm font-medium">{e.summary}</span>
                    </div>
                    <div className="text-xs text-[var(--fg-muted)]">
                      {e.source} · {formatDateTime(e.createdAt)}
                      {e.acknowledgedAt ? " · acknowledged" : ""}
                    </div>
                    {e.detail ? (
                      <pre className="mt-1 text-xs text-zinc-600 whitespace-pre-wrap break-words font-mono">
                        {e.detail}
                      </pre>
                    ) : null}
                    {target ? (
                      <Link
                        to={target.to}
                        className="mt-1 inline-block text-xs text-[var(--primary)] hover:underline"
                      >
                        {target.label} →
                      </Link>
                    ) : null}
                  </div>
                  {!e.acknowledgedAt ? (
                    <button
                      type="button"
                      onClick={() => ackM.mutate(e.id)}
                      disabled={ackM.isPending}
                      className="rounded-md border border-[var(--border)] px-2.5 py-1 text-xs hover:bg-zinc-50 disabled:opacity-50"
                    >
                      Acknowledge
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * The kind of failure, as a label an operator can read and click. Clicking
 * narrows the list to that code — the fastest way to answer "is this one thing
 * happening repeatedly, or did I just see it once?". Rows written before codes
 * existed fall back to their source and are not clickable.
 */
function KindBadge({
  label,
  code,
  onFilter,
}: {
  label: string;
  code: string | null;
  onFilter: () => void;
}) {
  const className =
    "inline-flex shrink-0 items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800";
  if (!code) return <span className={className}>{label}</span>;
  return (
    <button
      type="button"
      onClick={onFilter}
      title={`Show only ${code}`}
      className={`${className} hover:bg-sky-200`}
    >
      {label}
    </button>
  );
}

function SeverityPill({ severity }: { severity: ErrorSeverity }) {
  const tone =
    severity === "CRITICAL"
      ? "bg-rose-100 text-rose-700"
      : severity === "ERROR"
        ? "bg-amber-100 text-amber-800"
        : "bg-zinc-100 text-zinc-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${tone}`}
    >
      {severity}
    </span>
  );
}

/**
 * Hold a value back until typing stops, so the search box issues one request
 * per phrase instead of one per keystroke.
 */
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["errors"] });
  queryClient.invalidateQueries({ queryKey: ["errors-summary"] });
}
